import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchFAQ } from "../../Redux/WebsiteSlice";
import PageAboutSection from "../Shared/PageAboutSection";
import Reveal from "../Shared/Reveal";
import "./FAQ.scss";

const FAQ = () => {
  const dispatch = useDispatch();
  const faqs = useSelector((state) => state.website.faq);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    dispatch(fetchFAQ());
  }, [dispatch]);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const grouped = faqs.reduce((acc, faq) => {
    const cat = faq.category || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(faq);
    return acc;
  }, {});

  const defaultFAQs = [
    {
      question: "Do I need a referral to see a physiotherapist?",
      answer:
        "No, you do not need a referral to see a physiotherapist. You can book directly with us at any time. However, a referral may be required for certain funding schemes such as Medicare Enhanced Primary Care (EPC) plans.",
    },
    {
      question: "How long is an initial appointment?",
      answer:
        "Your initial appointment is typically 45–60 minutes. This allows your physiotherapist to conduct a thorough assessment, understand your history, and begin planning your treatment.",
    },
    {
      question: "What should I bring to my first appointment?",
      answer:
        "Please bring any relevant medical imaging (X-rays, MRI reports), your referral letter if applicable, your Medicare or health fund card, and wear comfortable, loose-fitting clothing appropriate for the area being treated.",
    },
    {
      question: "Is physiotherapy covered by private health insurance?",
      answer:
        "Yes, physiotherapy is covered by most private health funds under extras cover. The amount refunded depends on your level of cover. We recommend contacting your insurer to confirm your entitlements before your appointment.",
    },
    {
      question: "Do you treat NDIS participants?",
      answer:
        "Yes, we welcome NDIS participants. We can work with self-managed and plan-managed participants. Please contact us prior to your first appointment so we can ensure we have the appropriate documentation.",
    },
    {
      question: "How many sessions will I need?",
      answer:
        "The number of sessions varies depending on your condition, goals, and how your body responds to treatment. Your physiotherapist will discuss a recommended treatment plan at your initial appointment and review this with you regularly.",
    },
    {
      question: "Can I claim through Medicare?",
      answer:
        "Physiotherapy is covered under the Medicare Enhanced Primary Care (EPC) plan if your GP has referred you and you have a chronic condition. This provides a limited number of rebated sessions per calendar year.",
    },
    {
      question: "Do you offer online or telehealth consultations?",
      answer:
        "Yes, we offer telehealth consultations for certain conditions and follow-up appointments. Please contact us to discuss whether telehealth is suitable for your needs.",
    },
  ];

  const hasFAQs = faqs && faqs.length > 0;
  const displayItems = hasFAQs ? null : defaultFAQs;

  return (
    <div className="page-faq">
      <div className="page-hero">
        <div className="page-hero__content">
          <span className="small__text">NEED ANSWERS?</span>
          <h1 className="headline__text">
            Frequently Asked <span>Questions</span>
          </h1>
          <p className="page-hero__subtitle">
            Find answers to the most common questions about our services, bookings, and fees.
          </p>
        </div>
      </div>

      <PageAboutSection
        pageKey="faq"
        fallbackHeading="Got Questions?"
        fallbackParagraph="We've put together answers to the questions we hear most often. If you can't find what you're looking for, our team is always happy to help."
      />

      <div className="faq-section">
        <Reveal as="div" direction="up" className="faq-section__inner">
          {hasFAQs ? (
            Object.entries(grouped).map(([category, items]) => (
              <div key={category} className="faq-category">
                {Object.keys(grouped).length > 1 && (
                  <h2 className="faq-category__title">{category}</h2>
                )}
                <div className="faq-list">
                  {items.map((faq, index) => {
                    const globalIndex = `${category}-${index}`;
                    return (
                      <div
                        key={faq._id}
                        className={`faq-item ${openIndex === globalIndex ? "faq-item--open" : ""}`}
                      >
                        <button
                          className="faq-item__question"
                          onClick={() => toggle(globalIndex)}
                          aria-expanded={openIndex === globalIndex}
                        >
                          <span>{faq.question}</span>
                          <svg
                            className="faq-item__icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </button>
                        <div className="faq-item__answer">
                          <p>{faq.answer}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="faq-list">
              {displayItems.map((faq, index) => (
                <div
                  key={index}
                  className={`faq-item ${openIndex === index ? "faq-item--open" : ""}`}
                >
                  <button
                    className="faq-item__question"
                    onClick={() => toggle(index)}
                    aria-expanded={openIndex === index}
                  >
                    <span>{faq.question}</span>
                    <svg
                      className="faq-item__icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  <div className="faq-item__answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Reveal>
      </div>

      <div className="faq-cta">
        <Reveal as="div" direction="up" className="faq-cta__inner">
          <h2>Still have questions?</h2>
          <p>Our team is happy to help. Give us a call or send us a message.</p>
          <a href="/contact" className="site__button site__button_style--solid site__button_shape--default">
            <span className="text">Contact Us</span>
          </a>
        </Reveal>
      </div>
    </div>
  );
};

export default FAQ;
